<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  NxButton,
  NxSettingsToggle,
  NxToggleGroup,
  NxWordListEditor,
  downloadBlob,
  useNxConfirm,
  type NxWordListItem,
  type NxWordListMessage,
} from '@blpde/nx-ui';
import {
  spellNeverSuggestAllow,
  spellNeverSuggestEntries,
  spellNeverSuggestPatch,
  spellNeverSuggestRemoval,
  type SpellCheckConfig,
} from '../../config/spellCheck';
import {
  DICTIONARY_MAX_WORD,
  dictionaryFileText,
  dictionaryWord,
  parseDictionaryFile,
  type DictionaryLanguage,
} from '../dictionaryFile';
import { dictionaryFold } from '../dictionaryFold';
import { neverSuggestImportPreview } from '../dictionaryImport';
import type { DictionaryLevelHandle } from './level';
import { DICTIONARY_TEXT, originLabel } from './labels';
import { useDictionaryTabState } from './tabState';
import DictionaryFillHint from './DictionaryFillHint.vue';
import DictionaryImportModal from './DictionaryImportModal.vue';

/**
 * „Nie vorschlagen“ (I-3024): the words the search never suggests —
 * `spell_check.never_suggest` of one layer, per language. The kit's
 * `NxWordListEditor` is the list (add field, search, removal, export of a
 * word list); the import goes through the shared preview of
 * `DictionaryImportModal` (a plain word list, one line per word, into the
 * chosen language) like the corrections' and the synonyms' — the list's own
 * import button is not bound. A word of a farther layer shows its origin and
 * stays. „Alle wieder zulassen“ removes every own word in ONE patch. An
 * account's word also hides company and tenant words and corrections; on a
 * company or tenant layer the words hide for everyone below.
 */
const props = defineProps<{ level: DictionaryLevelHandle<SpellCheckConfig> }>();

const { t } = useI18n();
const confirm = useNxConfirm();
const T = DICTIONARY_TEXT;
const FIELD = 'never_suggest';
const state = useDictionaryTabState(props.level as DictionaryLevelHandle<unknown>, FIELD);

const lang = ref<DictionaryLanguage>('de');
const languages = computed(() => [
  { value: 'de' as const, label: t(`${T}.langs.de`) },
  { value: 'en' as const, label: t(`${T}.langs.en`) },
]);

const entries = computed(() =>
  spellNeverSuggestEntries(props.level.effective.value, props.level.layers.value, props.level.target.value),
);
const inLanguage = computed(() => entries.value.filter((entry) => entry.lang === lang.value));
const ownEntries = computed(() => entries.value.filter((entry) => entry.source === 'own'));
const editable = computed(() => !state.readOnly.value && state.layersKnown.value);

/** The rows of the kit's word list: a farther layer's word shows its origin and stays. */
const items = computed<NxWordListItem[]>(() =>
  inLanguage.value.map((entry) => {
    const foreign = entry.source !== 'own';
    return {
      word: entry.word,
      badge: foreign || entry.origin === 'import' ? originLabel(t, { origin: entry.origin, source: entry.source }) : undefined,
      badgeHint: foreign ? t(`${T}.origin.inheritedHint`) : undefined,
      removable: !foreign && editable.value,
    };
  }),
);

/* ── Adding and removing ── */

const draft = ref('');
const addHint = ref<string | null>(null);
watch(draft, () => {
  addHint.value = null;
});

function add(typed: string): void {
  const word = dictionaryWord(typed);
  if (!word) {
    addHint.value = t(`${T}.neverSuggest.tooLong`, { max: DICTIONARY_MAX_WORD });
    return;
  }
  const twin = inLanguage.value.find((entry) => dictionaryFold(entry.word) === dictionaryFold(word));
  if (twin) {
    addHint.value = t(`${T}.neverSuggest.duplicate`, { word: twin.word });
    return;
  }
  const patch = spellNeverSuggestPatch(lang.value, word);
  if (patch) void state.write(patch);
  draft.value = '';
}

function remove(word: string): void {
  void state.write(spellNeverSuggestRemoval(lang.value, word));
}

async function allowAll(): Promise<void> {
  const patch = spellNeverSuggestAllow(ownEntries.value);
  if (!patch) return;
  const confirmed = await confirm({
    title: t(`${T}.neverSuggest.allowAllTitle`),
    message: t(`${T}.neverSuggest.allowAllMessage`, { count: ownEntries.value.length }, ownEntries.value.length),
    confirmLabel: t(`${T}.neverSuggest.allowAllConfirm`),
    tone: 'danger',
  });
  if (confirmed) void state.write(patch);
}

/* ── Import and export ── */

const importOpen = ref(false);

/** What a word list would do to the written layer, in the chosen language. */
function previewImport(text: string) {
  const parsed = parseDictionaryFile(text);
  return neverSuggestImportPreview(parsed.words, lang.value, state.ownDocument.value ?? {}, parsed.invalid);
}

function exportFile(): void {
  downloadBlob(
    dictionaryFileText(ownEntries.value.filter((entry) => entry.lang === lang.value).map((entry) => entry.word)),
    t(`${T}.export.neverSuggest`),
  );
}

const messages = computed<NxWordListMessage[]>(() =>
  state.readOnlyText.value ? [{ id: 'locked', variant: 'info' as const, text: state.readOnlyText.value }] : [],
);

const hint = computed(() => t(`${T}.hints.neverSuggest.${props.level.key}`));
</script>

<template>
  <div class="dict-never" data-testid="dictionary-never-suggest">
    <p class="dict-note">{{ hint }}</p>
    <NxSettingsToggle
      v-if="level.setLock"
      :model-value="state.ownLocked.value"
      :label="t(`${T}.lock.label`)"
      :disabled="!editable"
      data-testid="dictionary-lock"
      @update:model-value="(on: boolean) => state.setOwnLock(on)"
    />
    <div class="dict-toolbar">
      <NxToggleGroup v-model="lang" :label="t(`${T}.langs.label`)" :options="languages" />
      <span class="dict-grow" />
      <NxButton variant="outline" data-testid="dictionary-import" :disabled="!editable" @click="importOpen = true">
        {{ t(`${T}.actions.import`) }}
      </NxButton>
      <NxButton
        variant="outline-danger"
        data-testid="dictionary-allow-all"
        :disabled="!editable || !ownEntries.length"
        @click="allowAll"
      >
        {{ t(`${T}.neverSuggest.allowAll`) }}
      </NxButton>
    </div>
    <NxWordListEditor
      v-model:draft="draft"
      :items="items"
      :label="t(`${T}.tabs.neverSuggest`)"
      :readonly="!editable"
      add-mode="button"
      :hint="addHint"
      :messages="messages"
      @add="add"
      @remove="remove"
      @export="exportFile"
    />
    <DictionaryFillHint :document="state.ownDocument.value" kind="spell" />
    <DictionaryImportModal
      v-model:open="importOpen"
      :title="t(`${T}.import.titleNeverSuggest`)"
      :hint="t(`${T}.import.formatNeverSuggest`, { lang: t(`${T}.langs.${lang}`) })"
      accept=".txt,.dic,text/plain"
      :preview="previewImport"
      :apply="(patch) => level.patch(patch)"
    />
  </div>
</template>

<style scoped>
.dict-never {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  min-width: 0;
}
.dict-note {
  margin: 0;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
}
.dict-toolbar {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-sm);
  flex-wrap: wrap;
}
.dict-grow {
  flex: 1;
}
</style>
